/**
 * PresetLoadDialog Component
 * Dialog for browsing and loading instrument presets
 */

import { useState, useEffect, useRef } from 'react';
import type { InstrumentType } from '../../types/composition';
import type { InstrumentPreset } from '../../types/instruments';
import { instrumentPresetManager } from '../../services/instruments/InstrumentPresetManager';
import './PresetDialogs.css';

/**
 * Props for PresetLoadDialog component
 */
interface PresetLoadDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Current instrument type */
  instrumentId: InstrumentType;
  /** Callback when preset is selected */
  onLoad: (presetId: string) => void;
}

/**
 * PresetLoadDialog component
 * Displays available presets with separate sections for default and user presets
 */
export function PresetLoadDialog({
  isOpen,
  onClose,
  instrumentId,
  onLoad,
}: PresetLoadDialogProps) {
  const [presets, setPresets] = useState<InstrumentPreset[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Load presets when dialog opens or instrument changes
  useEffect(() => {
    if (isOpen) {
      const loadedPresets = instrumentPresetManager.getPresetsForInstrument(instrumentId);
      setPresets(loadedPresets);
      setSelectedPresetId(null);
      setDeleteConfirmId(null);
    }
  }, [isOpen, instrumentId]);

  // Handle escape key to close dialog
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (deleteConfirmId) {
          setDeleteConfirmId(null);
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, deleteConfirmId, onClose]);

  // Handle click outside dialog to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen, onClose]);

  /**
   * Handle preset selection and loading
   */
  const handleLoadPreset = (presetId: string) => {
    onLoad(presetId);
    onClose();
  };

  /**
   * Handle preset deletion
   */
  const handleDeletePreset = (presetId: string) => {
    const success = instrumentPresetManager.deletePreset(presetId);
    
    if (success) {
      // Refresh preset list
      const updatedPresets = instrumentPresetManager.getPresetsForInstrument(instrumentId);
      setPresets(updatedPresets);
      setDeleteConfirmId(null);
    }
  };

  /**
   * Render a preset card
   */
  const renderPresetCard = (preset: InstrumentPreset) => {
    const isDeleting = deleteConfirmId === preset.id;

    return (
      <div
        key={preset.id}
        className={`preset-card ${selectedPresetId === preset.id ? 'preset-card-selected' : ''}`}
        onClick={() => setSelectedPresetId(preset.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelectedPresetId(preset.id);
          }
        }}
        aria-label={`${preset.name} preset`}
      >
        <div className="preset-card-header">
          <h3 className="preset-card-title">{preset.name}</h3>
          {preset.isUserCreated && (
            <span className="preset-card-badge">Custom</span>
          )}
        </div>

        {preset.description && (
          <p className="preset-card-description">{preset.description}</p>
        )}

        <div className="preset-card-actions">
          <button
            className="preset-card-btn preset-card-btn-load"
            onClick={(e) => {
              e.stopPropagation();
              handleLoadPreset(preset.id);
            }}
            aria-label={`Load ${preset.name} preset`}
          >
            Load
          </button>

          {preset.isUserCreated && (
            <>
              {isDeleting ? (
                <div className="preset-delete-confirm">
                  <span className="preset-delete-text">Delete?</span>
                  <button
                    className="preset-card-btn preset-card-btn-confirm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePreset(preset.id);
                    }}
                    aria-label="Confirm delete"
                  >
                    Yes
                  </button>
                  <button
                    className="preset-card-btn preset-card-btn-cancel"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirmId(null);
                    }}
                    aria-label="Cancel delete"
                  >
                    No
                  </button>
                </div>
              ) : (
                <button
                  className="preset-card-btn preset-card-btn-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirmId(preset.id);
                  }}
                  aria-label={`Delete ${preset.name} preset`}
                >
                  Delete
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  // Separate presets into default and user-created
  const defaultPresets = presets.filter((p) => p.isDefault);
  const userPresets = presets.filter((p) => p.isUserCreated);

  return (
    <div className="preset-dialog-overlay" role="dialog" aria-modal="true" aria-labelledby="load-dialog-title">
      <div className="preset-dialog preset-dialog-large" ref={dialogRef}>
        <div className="preset-dialog-header">
          <h2 id="load-dialog-title" className="preset-dialog-title">
            Load Preset
          </h2>
          <button
            className="preset-dialog-close"
            onClick={onClose}
            aria-label="Close dialog"
            type="button"
          >
            ✕
          </button>
        </div>

        <div className="preset-dialog-content">
          {/* Default Presets Section */}
          {defaultPresets.length > 0 && (
            <div className="preset-section">
              <h3 className="preset-section-title">Default Presets</h3>
              <div className="preset-grid">
                {defaultPresets.map(renderPresetCard)}
              </div>
            </div>
          )}

          {/* User Presets Section */}
          {userPresets.length > 0 && (
            <div className="preset-section">
              <h3 className="preset-section-title">My Presets</h3>
              <div className="preset-grid">
                {userPresets.map(renderPresetCard)}
              </div>
            </div>
          )}

          {/* Empty State */}
          {presets.length === 0 && (
            <div className="preset-empty-state">
              <p className="preset-empty-text">No presets available for this instrument.</p>
              <p className="preset-empty-hint">
                Create a custom preset by adjusting parameters and clicking "Save Preset".
              </p>
            </div>
          )}
        </div>

        <div className="preset-dialog-footer">
          <button
            className="preset-btn preset-btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
